import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ROUTES } from "@/app/routes";
import { AccountPicker } from "@/features/identity/components/account-picker";
import { LoginMagicLink } from "@/features/identity/components/login-magic-link";
import { LoginPasswordForm } from "@/features/identity/components/login-password-form";
import { useLoginStage } from "@/features/identity/hooks/use-login-stage";
import { useCompleteSignup } from "@/features/identity/hooks/use-signup-flow";
import {
  type AccountCandidate,
  loginWithPassword,
  pickLoginAccount,
  requestLoginToken,
  verifyLoginToken,
} from "@/features/identity/identity.api";
import { trackSignupStarted, trackSignupVerified } from "@/shared/analytics/registration-analytics";
import { rememberConsentReturnTo } from "@/shared/auth/oauth-return-to";
import { LogoLockup } from "@/shared/components/brand/logo-mark";

/// sessionStorage key for the in-flight email. Per FE-SEC7 the email stays
/// out of the URL (PII); per FE-SEC3 only auth tokens are forbidden from
/// session storage, a typed email is fine.
const EMAIL_STORAGE_KEY = "pw_login_email";

function readPersistedEmail(): string {
  try {
    return sessionStorage.getItem(EMAIL_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

function persistEmail(email: string): void {
  try {
    if (email) sessionStorage.setItem(EMAIL_STORAGE_KEY, email);
    else sessionStorage.removeItem(EMAIL_STORAGE_KEY);
  } catch {
    // ignore quota / disabled storage, degrades to in-memory only.
  }
}

/**
 * /login, existing-user login. Password is the primary method; a magic-link /
 * OTP flow is offered as an alternative.
 *
 * The magic-link flow stage lives in the URL search param `stage` (see
 * `useLoginStage`). The typed email is mirrored to `sessionStorage` so
 * refreshing the OTP screen still has an email to verify against, emails stay
 * out of the URL because they're PII (FE-SEC7).
 *
 * `returnTo` is set when the form renders inside the AI app consent screen:
 * every sign-in method then lands back there instead of on `/`.
 */
export function LoginRoute({ returnTo }: { returnTo?: string }) {
  const { stage, setStage } = useLoginStage(returnTo);
  const destination = returnTo ?? ROUTES.root.path;

  // Local UI toggle: password (default) vs the magic-link alternative. Kept in
  // component state, not the URL, the magic-link `stage` already lives there.
  const [method, setMethod] = useState<"password" | "magic-link">("password");
  const [email, setEmail] = useState<string>(readPersistedEmail);
  const [password, setPassword] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [picking, setPicking] = useState<{
    token: string;
    candidates: AccountCandidate[];
  } | null>(null);

  // reason: mirror email into sessionStorage so a refresh at the OTP stage
  // still has a value to verify against (URL holds the stage, storage holds
  // the email, neither path leaks PII into the URL).
  useEffect(() => {
    persistEmail(email);
  }, [email]);

  // Defence-in-depth: if the URL says check-email but we don't have an email
  // (e.g. someone deep-linked, or sessionStorage was cleared), bounce back
  // to enter-email so the form is in a coherent state.
  useEffect(() => {
    if (stage === "check-email" && email === "") void setStage("enter-email");
  }, [stage, email, setStage]);

  const request = useMutation({ mutationFn: requestLoginToken });
  const verify = useMutation({ mutationFn: verifyLoginToken });
  const passwordLogin = useMutation({ mutationFn: loginWithPassword });
  const pick = useMutation({ mutationFn: pickLoginAccount });
  const complete = useCompleteSignup("magic_link");

  function finishSignIn() {
    persistEmail("");
    // reason: hard reload (not SPA navigate) so the whole app rebuilds for the
    // new identity. An in-place query-cache swap left the previous user's
    // cached /auth/me in memory after a signout -> login in the same tab, the
    // new user then booted with the old user's name, role, and workspace.
    window.location.assign(destination);
  }

  async function onSubmitPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email || !password) return;
    try {
      await passwordLogin.mutateAsync({ email, password });
      finishSignIn();
    } catch {
      setError("That email and password don't match. Try again, or reset your password.");
    }
  }

  async function onSubmitEmail(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email) return;
    try {
      if (returnTo) rememberConsentReturnTo(returnTo);
      await request.mutateAsync({ email, returnTo });
      await setStage("check-email");
    } catch {
      setError("Could not send the link. Try again.");
    }
  }

  async function onSubmitOtp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (otpCode.length !== 6) {
      setError("Enter the 6-digit code from your email.");
      return;
    }
    try {
      const result = await verify.mutateAsync({ email, otpCode });
      if (result.action === "signed-in") {
        finishSignIn();
      } else if (result.action === "redirect-signup") {
        trackSignupStarted("magic_link");
        trackSignupVerified("magic_link");
        await complete.mutateAsync({ token: result.token });
        persistEmail("");
        window.location.assign(returnTo ?? "/?firstRun=1");
      } else {
        setPicking({ token: result.token, candidates: result.candidates });
      }
    } catch {
      setError("That code is invalid or expired. Request a new one.");
    }
  }

  async function onPickAccount(userId: string) {
    if (!picking) return;
    try {
      await pick.mutateAsync({ token: picking.token, userId });
      finishSignIn();
    } catch {
      // The pick token is single use and short lived, so start over.
      setPicking(null);
      setOtpCode("");
      setError("That sign-in expired. Request a new code.");
      await setStage("enter-email");
    }
  }

  if (picking) {
    return (
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 px-6 py-12">
        <LogoLockup className="w-fit" />
        <AccountPicker
          reason="Your email is on more than one Pulsewise account."
          candidates={picking.candidates}
          pending={pick.isPending}
          onPick={(userId) => void onPickAccount(userId)}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-8 px-6 py-12">
      <div>
        <LogoLockup className="w-fit" />
        <h1 className="font-display mt-4 text-3xl font-semibold tracking-tighter">Welcome back</h1>
        <p className="mt-2 text-sm text-text-secondary">Sign in with your email and password.</p>
      </div>

      {stage === "enter-email" && method === "password" ? (
        <LoginPasswordForm
          email={email}
          password={password}
          error={error}
          pending={passwordLogin.isPending}
          returnTo={returnTo}
          onEmailChange={setEmail}
          onPasswordChange={setPassword}
          onSubmit={onSubmitPassword}
          onUseMagicLink={() => {
            setMethod("magic-link");
            setError(null);
          }}
        />
      ) : (
        <LoginMagicLink
          stage={stage}
          email={email}
          otpCode={otpCode}
          error={error}
          requestPending={request.isPending}
          verifyPending={verify.isPending}
          onEmailChange={setEmail}
          onOtpChange={setOtpCode}
          onSubmitEmail={onSubmitEmail}
          onSubmitOtp={onSubmitOtp}
          onUsePassword={() => {
            setMethod("password");
            setError(null);
          }}
          onUseDifferentEmail={() => {
            setOtpCode("");
            setError(null);
            persistEmail("");
            setEmail("");
            void setStage("enter-email");
          }}
        />
      )}
    </div>
  );
}
